import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Users, FileText, Printer, MessageSquare, TrendingUp, Download, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/export-csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Users exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleExportFeedback = async () => {
    try {
      const response = await axios.get(`${API}/admin/feedback/export-csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'feedback_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Feedback exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleMarkRead = async (feedbackId, currentStatus) => {
    try {
      const endpoint = currentStatus === 'unread' ? 'mark-read' : 'mark-unread';
      await axios.put(`${API}/admin/feedback/${feedbackId}/${endpoint}`);
      // Update local state
      setStats(prev => ({
        ...prev,
        feedback: {
          ...prev.feedback,
          unread_count: currentStatus === 'unread' 
            ? prev.feedback.unread_count - 1 
            : prev.feedback.unread_count + 1,
          recent: prev.feedback.recent.map(f => 
            f.id === feedbackId ? { ...f, status: currentStatus === 'unread' ? 'read' : 'unread' } : f
          )
        }
      }));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Failed to load dashboard data</p>
        <Button onClick={fetchStats} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">What's New</h1>
          <p className="text-sm text-muted-foreground">Admin Dashboard</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportUsers} className="rounded-xl">
            <Download className="w-4 h-4 mr-2" />
            Export Users
          </Button>
          <Button variant="outline" onClick={handleExportFeedback} className="rounded-xl">
            <Download className="w-4 h-4 mr-2" />
            Export Feedback
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Users */}
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Users</p>
              <p className="text-2xl font-semibold">{stats.users.total}</p>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-green-500" />
              <span className="text-muted-foreground">7d:</span>
              <span className="font-medium">{stats.users.last_7_days}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">30d:</span>
              <span className="font-medium">{stats.users.last_30_days}</span>
            </div>
          </div>
        </div>

        {/* Rubrics */}
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Rubrics Created</p>
              <p className="text-2xl font-semibold">{stats.rubrics.total}</p>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-green-500" />
              <span className="text-muted-foreground">7d:</span>
              <span className="font-medium">{stats.rubrics.last_7_days}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">30d:</span>
              <span className="font-medium">{stats.rubrics.last_30_days}</span>
            </div>
          </div>
        </div>

        {/* Prints */}
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Printer className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Prints Generated</p>
              <p className="text-2xl font-semibold">{stats.prints.total}</p>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-green-500" />
              <span className="text-muted-foreground">7d:</span>
              <span className="font-medium">{stats.prints.last_7_days}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">30d:</span>
              <span className="font-medium">{stats.prints.last_30_days}</span>
            </div>
          </div>
        </div>

        {/* Feedback */}
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Unread Feedback</p>
              <p className="text-2xl font-semibold">{stats.feedback.unread_count}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {stats.feedback.recent.length} recent submissions
          </p>
        </div>
      </div>

      {/* Recent Feedback */}
      <div className="bg-white rounded-xl border border-border">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            Recent Feedback
            {stats.feedback.unread_count > 0 && (
              <span className="px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                {stats.feedback.unread_count} new
              </span>
            )}
          </h2>
        </div>
        <div className="divide-y divide-border">
          {stats.feedback.recent.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No feedback yet
            </div>
          ) : (
            stats.feedback.recent.map((feedback) => (
              <div 
                key={feedback.id} 
                className={`p-4 flex items-start gap-4 ${
                  feedback.status === 'unread' ? 'bg-primary/5' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      feedback.status === 'unread' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted'
                    }`}>
                      {feedback.feedback_type.replace('_', ' ')}
                    </span>
                    {feedback.category && (
                      <span className="text-xs text-muted-foreground">
                        {feedback.category}
                      </span>
                    )}
                  </div>
                  <p className="text-sm line-clamp-2">{feedback.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span>{feedback.user_email}</span>
                    <span>•</span>
                    <span>{new Date(feedback.created_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{feedback.upvotes} upvotes</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMarkRead(feedback.id, feedback.status)}
                  className="flex-shrink-0"
                >
                  {feedback.status === 'unread' ? (
                    <Eye className="w-4 h-4" />
                  ) : (
                    <EyeOff className="w-4 h-4" />
                  )}
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
